import { AutoSaveIndicator } from '@/components/common/auto-save-indicator';
import { LoadingBlockFullPage } from '@/components/loading/loading-block-full-page';
import { Button } from '@/components/ui/button';
import { DrawerContainer, DrawerContent, DrawerHeader } from '@/components/ui/drawer';
import { Notice } from '@/components/ui/notice';
import { Text } from '@/components/ui/text';
import { useAppConfig } from '@/features/acl/acl-provider';
import { useAnalytics } from '@/features/analytics/hooks/use-analytics';
import { CommentsThread } from '@/features/comments/components/comments-thread';
import { DialogStateManager } from '@/features/dialog/dialog-provider';
import { useUpdateNote } from '@/features/notes/hooks/use-update-note';
import { TraskCreateDialog } from '@/features/tasks/components/task-create-dialog';
import { useFormAutoSave } from '@/lib/hooks/use-form-auto-save';
import { useLocale } from '@/lib/hooks/use-locale';
import type { CrmEventDetails } from '@/server/types/infer-trpc-types';
import { SearchEntity } from '@/server/types/search-types';
import { api } from '@/utils/api';
import { getEventMetadata, hasOccurrenceOn } from '@/utils/event-utils';
import { PlusIcon } from '@heroicons/react/24/outline';
import { intlFormat, isFuture, isSameDay } from 'date-fns';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useUpdateEvent } from '../hooks/use-update-event';
import { getInputFromCrmEventDetails } from '../utils/get-input-from-crm-event-details';
import type { EventFormData } from './event-form-fields';
import { EventFormFields } from './event-form-fields';
import { EventNoteCreate } from './event-note-create';
import { EventNoteForm } from './event-note-form';
import { EventOptionsDropdown } from './event-options-dropdown';

export interface EventDetailsFormProps {
    eventDetails: CrmEventDetails;
    onUpdate: (values: EventFormData) => void;
    disabled?: boolean;
}

const EventDetailsForm = ({ eventDetails, onUpdate, disabled = false }: EventDetailsFormProps) => {
    const { t } = useLocale('events');

    const form = useForm<EventFormData>({
        mode: 'onChange',
        defaultValues: getInputFromCrmEventDetails(eventDetails),
        disabled,
    });

    const { reset, watch } = form;

    useEffect(() => {
        reset(getInputFromCrmEventDetails(eventDetails), { keepValues: true });
    }, [eventDetails, reset]);

    // Auto-save when form values change and the form is dirty (only when not disabled)
    useFormAutoSave(
        watch,
        (values) => {
            if (!disabled) {
                onUpdate(values as EventFormData);
            }
        },
        1000,
    );

    return (
        <div className="flex flex-col gap-3">
            {!!watch('recurrence') && (
                <Notice
                    variant="info"
                    title={t('events__recurrence_notice_title')}
                    description={t('events__recurrence_notice_description')}
                />
            )}
            <div className="rounded-lg bg-white py-2 px-3 shadow-xs">
                <EventFormFields
                    includeLinks
                    form={form}
                    customer={eventDetails?.customer}
                    ownerName={eventDetails?.owner_name ?? ''}
                />
            </div>
        </div>
    );
};

export interface EventDetailsProps {
    date: string;
    eventId: string;
    className?: string;
    onBack?: () => void;
    onClose?: () => void;
    onUpdated?: () => void;
}

export const EventDetails = ({ date, eventId, className, onBack, onClose, onUpdated }: EventDetailsProps) => {
    const { t, locale } = useLocale('events');
    const sendEvent = useAnalytics();
    const { userId } = useAppConfig();

    // Use the direct getEventById endpoint
    const { data: eventDetails, isLoading } = api.platoUser.query.events.getEventById.useQuery(
        {
            eventId,
        },
        {
            enabled: !!eventId,
        },
    );

    const updateEventMutation = useUpdateEvent({
        onSuccess: () => {
            onUpdated?.();
        },
    });

    const updateNoteMutation = useUpdateNote({
        onSuccess: () => {},
    });

    // Determine overall autosave state
    const isUpdating = updateEventMutation.isLoading || updateNoteMutation.isLoading;
    const isSuccess = updateEventMutation.isSuccess || updateNoteMutation.isSuccess;
    const isError = updateEventMutation.isError || updateNoteMutation.isError;

    if (isLoading || !eventDetails) {
        return <LoadingBlockFullPage className="mt-64" />;
    }

    // Determine if the current user is the owner of the event
    const isOwner = eventDetails.owner_id === userId;

    const occurenceDate = new Date(date);
    const isOccurrence = hasOccurrenceOn(eventDetails, date);
    const noteDetails = eventDetails.notes.find(
        (note) => note.businessCreatedAt && isSameDay(note.businessCreatedAt, occurenceDate),
    );

    return (
        <DrawerContainer className={className}>
            <DrawerHeader
                onBack={onBack}
                onClose={onClose}
                actions={
                    eventDetails &&
                    isOwner && (
                        <>
                            <Button
                                variant="subtle"
                                icon={PlusIcon}
                                onClick={() => {
                                    DialogStateManager.show(TraskCreateDialog, {
                                        links: eventDetails.customer?.customerId
                                            ? [
                                                  {
                                                      modelType: SearchEntity.Customer,
                                                      modelId: eventDetails.customer.customerId,
                                                      title: eventDetails.customer.customerName,
                                                  },
                                              ]
                                            : undefined,
                                    });
                                }}
                            >
                                {t('details__new_task', { ns: 'events' })}
                            </Button>
                            <EventOptionsDropdown
                                date={date}
                                event={eventDetails}
                                onDelete={() => {
                                    onUpdated?.();
                                    onClose?.();
                                    sendEvent('event_deleted', getEventMetadata(eventDetails));
                                }}
                            />
                        </>
                    )
                }
                title={t('details__title')}
            >
                <AutoSaveIndicator isUpdating={isUpdating} isSuccess={isSuccess} isError={isError} />
            </DrawerHeader>
            <DrawerContent>
                <EventDetailsForm
                    eventDetails={eventDetails}
                    onUpdate={(data) =>
                        updateEventMutation.mutate({
                            eventId,
                            data,
                        })
                    }
                    disabled={!isOwner}
                />

                {isOccurrence && (noteDetails || !isFuture(occurenceDate)) && (
                    <div className="flex flex-col gap-3">
                        <Text color="neutral-950" weight="medium">
                            {t('note_for_date', {
                                date: intlFormat(
                                    occurenceDate,
                                    {
                                        month: 'long',
                                        day: 'numeric',
                                        year: 'numeric',
                                    },
                                    { locale },
                                ),
                            })}
                        </Text>

                        {noteDetails ? (
                            <div className="flex flex-col gap-3">
                                {/* Display the editable note */}
                                <EventNoteForm
                                    note={noteDetails}
                                    onUpdate={(values) => {
                                        updateNoteMutation.mutate({
                                            noteId: noteDetails.noteId,
                                            title: values.title,
                                        });
                                    }}
                                />

                                {/* Show comments thread */}
                                <CommentsThread noteId={noteDetails.noteId} />
                            </div>
                        ) : (
                            <EventNoteCreate event={eventDetails} occurrenceDate={date} />
                        )}
                    </div>
                )}
            </DrawerContent>
        </DrawerContainer>
    );
};
